import { css } from 'aphrodite';
import * as React from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { MastheadPortalId } from '../../../models/AdminModels';
import type { ISupportMailbox } from '../../../queries/Support/useSupportMailboxQuery';
import { useSupportMailboxListQuery, useSupportMailboxQuery } from '../../../queries/Support/useSupportMailboxQuery';
import { Portal } from '../../../web/components/Portal';
import { Button } from '../../../web/components-v2/Button';
import { TabToggle } from '../../../web/components-v2/TabToggle';
import { AdminSupportPageLayout } from '../../components/supportShared/AdminSupportPageLayout';
import { SupportMastheadAppBar } from '../../components/supportShared/SupportMastheadAppBar';
import { supportSharedStyles as ss } from '../../components/supportShared/styles/supportSharedStyles';
import { CategoriesTab } from './CategoriesTab';
import { MailboxEditForm } from './MailboxEditForm';
import { MailboxTab } from './MailboxTab';
import { OutgoingEmailTab } from './OutgoingEmailTab';
import { SavedRepliesTab } from './SavedRepliesTab';
import { adminSupportSettingsPageStyles as styles } from './styles/adminSupportSettingsPageStyles';
import { TeamTab } from './TeamTab';

/** Per-mailbox settings sections. `general` is the inbound/behavior config (MailboxEditForm). */
const detailSections = [
	{ key: 'general', label: 'General' },
	{ key: 'outgoing-email', label: 'Outgoing Email' },
	{ key: 'team', label: 'Team & Routing' },
	{ key: 'saved-replies', label: 'Saved Replies' },
	{ key: 'categories', label: 'Categories' },
];

const detailSectionItems = detailSections.map(item => ({ value: item.key, label: item.label }));

/** Left-pointing arrow used by the sidebar "back" affordance. */
function BackArrowIcon() {
	return (
		<svg
			width='15'
			height='15'
			viewBox='0 0 24 24'
			fill='none'
			stroke='currentColor'
			strokeWidth='2'
			strokeLinecap='round'
			strokeLinejoin='round'
			aria-hidden
		>
			<path d='M19 12H5M12 19l-7-7 7-7' />
		</svg>
	);
}

const getSelectedMailboxId = (mailboxes: ISupportMailbox[], requestedMailboxId?: string) => {
	if (requestedMailboxId && mailboxes.some(mailbox => mailbox.id === requestedMailboxId)) return requestedMailboxId;
	return mailboxes.find(mailbox => mailbox.isActive)?.id ?? mailboxes[0]?.id ?? '';
};

const buildSupportPath = (mailboxId: string, screen?: 'reports') => {
	const params = new URLSearchParams();
	if (mailboxId) params.set('mailboxId', mailboxId);
	if (screen === 'reports') params.set('screen', 'reports');
	const search = params.toString();
	return search ? `/support?${search}` : '/support';
};

/** Top-level support tabs for settings pages; breadcrumbs stay out of the masthead. */
function SupportSettingsMasthead({
	selectedMailboxId,
	showMailboxSwitcher,
}: {
	selectedMailboxId?: string;
	showMailboxSwitcher?: boolean;
}) {
	const history = useHistory();
	const location = useLocation();
	const { data: mailboxes } = useSupportMailboxListQuery();
	const mailboxList = mailboxes ?? [];
	const queryMailboxId = new URLSearchParams(location.search).get('mailboxId') || undefined;
	const resolvedMailboxId = getSelectedMailboxId(mailboxList, selectedMailboxId ?? queryMailboxId);
	const settingsPath = resolvedMailboxId
		? `/support/settings?mailboxId=${encodeURIComponent(resolvedMailboxId)}`
		: '/support/settings';

	return (
		<Portal destination={MastheadPortalId}>
			<SupportMastheadAppBar
				activeTab='settings'
				mailboxes={mailboxList}
				selectedMailboxId={resolvedMailboxId}
				onSelectInbox={() => history.push(buildSupportPath(resolvedMailboxId))}
				onSelectReports={() => history.push(buildSupportPath(resolvedMailboxId, 'reports'))}
				onSelectSettings={() => history.push(settingsPath)}
				onSelectMailbox={id => history.push(`/support/settings/${id}/general`)}
				showMailboxSwitcher={showMailboxSwitcher}
			/>
		</Portal>
	);
}

/**
 * Support settings, structured master→detail:
 *  - `/support/settings` → the mailbox list (select / create / deactivate).
 *  - `/support/settings/:mailboxId/:section?` → that mailbox's settings, every
 *    section scoped to the mailbox in the route. All inbound + outbound config
 *    lives on the mailbox, so there is no global "one for all" outgoing config.
 */
export function AdminSupportSettingsPage() {
	const { mailboxId, section } = useParams<{ mailboxId?: string; section?: string }>();

	if (mailboxId) {
		return <MailboxSettingsDetail mailboxId={mailboxId} section={section || 'general'} />;
	}
	return <MailboxListView />;
}

/** Settings landing: the list of mailboxes the caller can manage. */
function MailboxListView() {
	const history = useHistory();

	return (
		<AdminSupportPageLayout>
			<SupportSettingsMasthead showMailboxSwitcher={false} />
			<div className={css(ss.landingScroll)}>
				<div className={css(ss.landing)}>
					<MailboxTab onSelectMailbox={id => history.push(`/support/settings/${id}/general`)} />
				</div>
			</div>
		</AdminSupportPageLayout>
	);
}

/** A single mailbox's settings, with a section sub-nav scoped to that mailbox. */
function MailboxSettingsDetail({ mailboxId, section }: { mailboxId: string; section: string }) {
	const history = useHistory();
	const { data: mailbox } = useSupportMailboxQuery(mailboxId);
	const mailboxName = mailbox?.name || 'Mailbox';
	const activeSection = detailSections.some(s => s.key === section) ? section : 'general';
	const handleSectionChange = (nextSection: string) => {
		history.push(`/support/settings/${mailboxId}/${nextSection}`);
	};

	const renderSection = () => {
		switch (activeSection) {
			case 'outgoing-email':
				return <OutgoingEmailTab mailboxId={mailboxId} />;
			case 'team':
				return <TeamTab mailboxId={mailboxId} />;
			case 'saved-replies':
				return <SavedRepliesTab mailboxId={mailboxId} />;
			case 'categories':
				return <CategoriesTab mailboxId={mailboxId} />;
			default:
				// The sidebar already provides "← Mailboxes"; don't render MailboxEditForm's own back button.
				return <MailboxEditForm mailboxId={mailboxId} />;
		}
	};

	return (
		<AdminSupportPageLayout>
			<SupportSettingsMasthead selectedMailboxId={mailboxId} />
			<div className={css(ss.layout)}>
				<aside className={css(ss.leftSidebar)}>
					<Button
						type='button'
						variant='ghost'
						size='sm'
						styles={[styles.backButton]}
						onClick={() => history.push('/support/settings')}
					>
						<BackArrowIcon />
						Mailboxes
					</Button>
					<div className={css(ss.navSectionTitle)} title={mailboxName}>
						{mailboxName}
					</div>
					<nav className={css(ss.navSection)}>
						<TabToggle
							aria-label={`${mailboxName} settings sections`}
							items={detailSectionItems}
							value={activeSection}
							onValueChange={handleSectionChange}
							orientation='vertical'
							variant='sidebar'
						/>
					</nav>
				</aside>
				<div className={css(ss.content)}>{renderSection()}</div>
			</div>
		</AdminSupportPageLayout>
	);
}
